/**
 * Module dependencies.
 */

import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { media } from '@untile/react-core/styles/media';
import { transparentize } from 'src/styles/utils/colors';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useSchedule } from 'src/providers/schedule';
import { useTranslation } from 'next-i18next';
import editSmallSvg from 'src/assets/svgs/edit-xs.svg';
import editSvg from 'src/assets/svgs/edit.svg';
import pinSvg from 'src/assets/svgs/pin.svg';
import resumeSvg from 'src/assets/svgs/resume.svg';
import styled from 'styled-components';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-column-gap: var(--space-xxs);
  grid-template-columns: repeat(2, 1fr);
  padding: 0 var(--space-xxs);

  ${media.min.md`
    display: grid;
    grid-auto-rows: max-content;
    grid-column-gap: 0;
    grid-template-columns: 1fr;
    padding: 0;
  `}
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg).attrs({ size: '32px' })`
  color: var(--color-primary);
  grid-area: icon;
`;

/**
 * `EditIcon` styled component.
 */

const EditIcon = styled(Svg).attrs({ icon: editSmallSvg, size: '16px' })`
  color: var(--color-slate300);
  grid-area: editIcon;
`;

/**
 * `Button` styled component.
 */

const Button = styled.button`
  align-items: center;
  background-color: ${transparentize('cyan800', 0.16)};
  border: 2px solid transparent;
  border-radius: var(--border-radius-xs);
  color: var(--color-slate50);
  display: grid;
  gap: var(--space-xs);
  grid-auto-columns: max-content;
  grid-template-areas: 'name icon';
  justify-self: end;
  margin-bottom: var(--space-sm);
  padding: var(--space-xs);
  transition: var(--transition-default);
  transition-property: background-color, border-color, color;

  ${media.max.md`
    grid-template-areas: 'icon' 'name';
  `};

  :focus,
  :focus-within,
  :hover {
    background-color: ${transparentize('cyan800', 0.3)};
    border-color: ${transparentize('cyan400', 0.2)};
  }

  &[data-active='true'] {
    border-color: var(--color-cyan400);
  }

  :disabled {
    background-color: ${transparentize('cyan800', 0.16)};
    border-color: transparent;
    pointer-events: none;

    &,
    ${Icon} {
      color: var(--color-slate500);
    }
  }

  ${media.max.md`
    gap: var(--space-xxs);
    grid-auto-rows: min-content;
    grid-template-columns: 100%;
    justify-items: center;
    width: 100%;
  `}

  &[data-editable='true'] {
    grid-template-areas: 'name editIcon icon';

    ${media.max.md`
      grid-template-areas: 'icon' 'name' 'editIcon';
    `}
  }
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text).attrs({ variant: 'bodyM' })`
  display: grid;
  font-weight: 600;

  ${media.min.md`
    text-align: left;
  `}
`;

/**
 * Export `StepsProgress` component.
 */

export const StepsProgress = () => {
  const { t } = useTranslation('schedule');
  const { formData, setStep, step } = useSchedule();
  const isMobile = useBreakpoint(0, 'md');

  return (
    <Wrapper aria-label={t('labels:selectLocation')}>
      <Button data-active={step === 1} data-editable={step !== 1} {...(step !== 1 && { onClick: () => setStep(1) })}>
        <Label>{step !== 1 ? formData.county.countyName : t('labels.selectLocation')}</Label>

        {step !== 1 && <EditIcon />}

        <Icon icon={pinSvg} />
      </Button>

      <Button
        data-active={step === 2}
        data-editable={step === 3}
        {...(step !== 2 && { onClick: () => setStep(2) })}
        disabled={step === 1}
      >
        <Label>{t('schedule:labels.scheduleData')}</Label>

        {step === 3 && <EditIcon />}

        <Icon icon={editSvg} />
      </Button>

      {!isMobile && (
        <Button data-active={step === 3} disabled={step !== 3}>
          <Label>{t('labels.scheduleConfirm')}</Label>

          <Icon icon={resumeSvg} />
        </Button>
      )}
    </Wrapper>
  );
};
